import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppShell from '../components/AppShell';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ── Telling Doorly you cannot reach your agent ──────────────────────────────
// 🚨 THIS IS SOMEBODY ALREADY FRUSTRATED, AND THE WRONG TONE TURNS A REPORT INTO
// A COMPLAINT ABOUT DOORLY. So it opens with what Doorly will DO, not with a
// filing receipt — "your report has been submitted" is the sentence that makes a
// borrower feel handled rather than helped.
//
// ⚠️ IT PROMISES NO NEW AGENT. §2.5 lets Doorly assume the Broker Services only
// after two notices, each delivered, each unanswered, each expired — so nothing
// here may describe an outcome those preconditions have not reached. But the
// honest version of that reads as a brush-off unless it also says a change CAN
// follow, which is why both halves are in one sentence.
//
// ⚠️ "ABOUT A WEEK" RATHER THAN A DATE. Two notices × two Business Days, measured
// from DELIVERY, and delivery is not guaranteed same-day. A specific date would be
// a promise the clause does not support.
export default function AgentUnreachable() {
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [info, setInfo] = useState(null);
  const [attempts, setAttempts] = useState('');
  const [need, setNeed] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/borrower/agent-unreachable`, { headers })
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ applicable: false }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!info) return <AppShell><div style={{ padding: 28, color: '#9ca3af' }}>Loading…</div></AppShell>;

  // A borrower with no agent has nobody to report. They should not have reached
  // this page — the entry point is hidden — so this is the direct-URL case.
  if (!info.applicable) {
    return (
      <AppShell>
        <div style={{ padding: '28px 32px', maxWidth: 640, margin: '0 auto' }}>
          <p style={{ fontSize: 14, color: '#6b7280' }}>
            There is no agent on your file. If you need help with anything, reply to any Doorly
            email and a person will pick it up.
          </p>
        </div>
      </AppShell>
    );
  }

  if (sent) {
    return (
      <AppShell>
        <div style={{ padding: '28px 32px', maxWidth: 640, margin: '0 auto' }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
            We&rsquo;ve got it
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.7, color: '#374151', marginBottom: 12 }}>
            {/* ⚠️ NO "TODAY". A timing promise that breaks if the report lands at
                11pm or the escalation runs on a schedule. The first notice is in
                fact sent synchronously, but the sentence must stay true if that
                ever changes. */}
            We&rsquo;ll contact <strong>{sent.agent_name}</strong> and let you know when we do. If
            they don&rsquo;t respond within two business days, we&rsquo;ll send a second notice and
            tell you that too.
          </p>
          <p style={{ fontSize: 14, lineHeight: 1.7, color: '#374151' }}>
            In the meantime you can keep browsing and start a purchase whenever you&rsquo;re ready —
            none of that waits on your agent.
          </p>
          <button onClick={() => navigate('/dashboard')}
            style={{ marginTop: 20, background: '#3a6ad9', color: '#fff', border: 'none',
              borderRadius: 999, padding: '11px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Back to your dashboard
          </button>
        </div>
      </AppShell>
    );
  }

  const submit = async () => {
    setSending(true); setError('');
    try {
      const r = await axios.post(`${API}/api/borrower/agent-unreachable`,
        { attempts, need }, { headers });
      setSent(r.data);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || 'Could not send that.');
    } finally { setSending(false); }
  };

  const box = {
    width: '100%', minHeight: 92, padding: '10px 12px', fontSize: 14, lineHeight: 1.5,
    border: '1px solid #d1d5db', borderRadius: 10, fontFamily: 'inherit', resize: 'vertical',
  };

  return (
    <AppShell>
      <div style={{ padding: '28px 32px', maxWidth: 640, margin: '0 auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 10 }}>
          Tell us you can&rsquo;t reach your agent
        </h1>
        <p style={{ fontSize: 14, lineHeight: 1.7, color: '#374151', marginBottom: 20 }}>
          We&rsquo;ll contact them on your behalf. This is a report, not a request for a new agent —
          but it&rsquo;s how a change gets started if they stay unreachable.
        </p>

        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12,
          padding: '14px 18px', marginBottom: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
            What happens next
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.7, color: '#4b5563', margin: 0 }}>
            We email your agent and ask them to get in touch with you. If we don&rsquo;t hear back,
            we send a second notice. Each one gives them two business days to respond, so this takes{' '}
            <strong>about a week</strong> to run its course. We&rsquo;ll email you when we contact
            them, and again when we hear back — or when we don&rsquo;t.
          </p>
        </div>

        <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
          When did you last try to reach them, and how?{' '}
          <span style={{ fontWeight: 400, color: '#6b7280' }}>(your words — we&rsquo;ll quote this to them)</span>
        </label>
        <textarea value={attempts} onChange={(e) => setAttempts(e.target.value)} style={box}
          placeholder="e.g. Called Tuesday 9th and left a voicemail, texted Thursday, no reply to either." />

        <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#111827',
          margin: '18px 0 6px' }}>
          What do you need from them right now?
        </label>
        <textarea value={need} onChange={(e) => setNeed(e.target.value)} style={box}
          placeholder="e.g. I want to see 412 Oak before the weekend and I can't get an answer." />

        {/* ⚠️ SAID PLAINLY, because Doorly cannot verify any of it and a screen
            implying otherwise would misrepresent the record if it is ever
            disputed. What IS a fact is that they asserted it at a recorded time. */}
        <p style={{ fontSize: 12, lineHeight: 1.6, color: '#6b7280', margin: '14px 0 0' }}>
          We record what you&rsquo;ve written exactly as you&rsquo;ve written it. We don&rsquo;t
          verify it independently — it&rsquo;s your account of what happened, and that&rsquo;s what
          we&rsquo;ll act on.
        </p>

        <button onClick={submit} disabled={!attempts.trim() || sending}
          style={{ marginTop: 18, background: attempts.trim() ? '#3a6ad9' : '#e5e7eb',
            color: attempts.trim() ? '#fff' : '#9ca3af', border: 'none', borderRadius: 999,
            padding: '11px 24px', fontSize: 14, fontWeight: 600,
            cursor: attempts.trim() && !sending ? 'pointer' : 'not-allowed' }}>
          {sending ? 'Sending…' : 'Send this to Doorly'}
        </button>
        {error && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 10 }}>{error}</p>}

        {/* ⚠️ THE AGENT IS NAMED, WITH A CORRECTION PATH. Assignment moves;
            contacting the wrong person is worse than contacting nobody. */}
        <p style={{ fontSize: 12, lineHeight: 1.6, color: '#6b7280', marginTop: 22,
          paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
          Your agent is <strong>{info.agent_name}</strong>
          {info.brokerage ? `, ${info.brokerage}` : ''}. If that&rsquo;s wrong, tell us — we&rsquo;ll
          check the file rather than contact the wrong person.
        </p>
      </div>
    </AppShell>
  );
}
